import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { ZoneStatus } from '@/__generated__/graphql';

const ProgressBarColors = {
  Green: 'rgba(45, 201, 78, 0.7)',
  Blue: 'rgba(39, 96, 245, 0.7)',
  Purple: 'rgba(185, 134, 249, 1)',
  Yellow: 'rgba(253, 202, 74, 0.7)',
  Gray: 'rgba(91, 101, 128, 0.2)',
};

type BarProps = {
  width: number;
  height: number;
  color: string;
};
const Bar = ({ width, height, color }: BarProps) => {
  const barStyle = {
    width: `${width}%`,
    height: `${height}px`,
    backgroundColor: color,
  };

  return <Box sx={barStyle} />;
};

type ZoneStatusMapping = {
  [key in ZoneStatus]: {
    label: string;
    color: string;
  };
};

const ZoneStatusColorMapper: ZoneStatusMapping = {
  [ZoneStatus.Completed]: { color: ProgressBarColors.Green, label: 'Completed' },
  [ZoneStatus.InProgress]: { color: ProgressBarColors.Yellow, label: 'In Progress' },
  [ZoneStatus.InReview]: { color: ProgressBarColors.Blue, label: 'In Review' },
  [ZoneStatus.NotStarted]: { color: ProgressBarColors.Gray, label: 'Not Started' },
  [ZoneStatus.ReviewReady]: { color: ProgressBarColors.Purple, label: 'Ready for Review' },
};

type Props = {
  data: {
    status: ZoneStatus;
    numberOfZones: number;
  }[];
};

const indicatorCircleStyles = (status: ZoneStatus) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: ZoneStatusColorMapper[status]?.color,
  ml: 1,
});

const rootStyles = {
  my: 2,
};

const barContainerStyles = {
  display: 'flex',
  gap: '4px',
};

const indicatorContainerStyles = {
  display: 'flex',
  gap: 5,
};

const indicatorStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const headerStyles = {
  display: 'flex',
  my: 2,
};

export const ProgressBar = ({ data }: Props) => {
  const statusAndZones = data
    .map(({ status, numberOfZones }) => {
      if (status === ZoneStatus.InReview) {
        // find the item with status ReviewReady
        // and add the numberOfZones on it
        // to the current item
        const reviewReadyItem = data.find((item) => item.status === ZoneStatus.ReviewReady);
        if (reviewReadyItem) {
          return {
            status,
            numberOfZones: numberOfZones + reviewReadyItem.numberOfZones,
          };
        }
      }
      return { status, numberOfZones };
    })
    .filter((item) => item.status !== ZoneStatus.ReviewReady);

  const totalNumberOfZones = data.reduce((accumulator, item) => {
    return accumulator + item.numberOfZones;
  }, 0);

  return (
    <Box sx={rootStyles}>
      <Box sx={barContainerStyles}>
        {statusAndZones.map(({ status, numberOfZones }) => {
          const percentage = (numberOfZones / totalNumberOfZones) * 100;
          const zoneColor = ZoneStatusColorMapper[status]?.color;

          return <Bar key={status} width={percentage} height={40} color={zoneColor} />;
        })}
      </Box>
      <Box sx={indicatorContainerStyles}>
        <Box sx={headerStyles}>
          <Typography>Total:</Typography>
          <Typography>{totalNumberOfZones}</Typography>
        </Box>

        {statusAndZones.map(({ status, numberOfZones }) => (
          <Box sx={indicatorStyles} key={status}>
            <Box sx={indicatorCircleStyles(status)} />
            <Typography>{ZoneStatusColorMapper[status]?.label}</Typography>
            <Typography>({numberOfZones})</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
